/**
 * Task 292C3 — writer AI generation panel.
 *
 * Renders inside `ContentWriterWorkspace` as a collapsible section that
 * lets a `content_writer` pick one of their currently-assigned ACTIVE
 * clients and generate a calendar draft through the writer-scoped EF.
 * On successful save the parent workspace refreshes; the writer then
 * edits the seeded canonical variant text and submits to senior review
 * via the existing 292B3 atomic path.
 *
 * Every gate is server-authoritative. This panel:
 *   * NEVER trusts a client_id from the URL or browser state — the
 *     picker is populated from `me.client_assignments` and the server
 *     re-derives scope on both the generate EF and the save RPC.
 *   * NEVER exposes an approve / send-to-client / production / publish
 *     action — those are outside the writer's role by design.
 *   * NEVER opens a client the writer has no live active assignment to
 *     — even if the browser cache is stale, the server refuses.
 */

import { useRef, useState } from 'react';
import type { ClientAssignmentSummary, GenerateWriterAiDraftsData, MeContext } from '../api/types';
import type { ContentVariantPlatform } from '../api/types';
import { generateWriterAiDrafts, saveWriterAiCalendarDraft } from '../api/endpoints';
import {
  activeAssignedClientsForWriter,
  buildWriterAiSaveItems,
  currentMonthYYYYMM,
  platformLabelAr,
  WRITER_AI_GENERATING_AR,
  WRITER_AI_GENERIC_ERROR_AR,
  WRITER_AI_NO_CLIENTS_AR,
  WRITER_AI_PICK_CLIENT_AR,
  WRITER_AI_SAVING_AR,
  WRITER_AI_SUCCESS_AR,
  WRITER_AI_TITLE_AR,
} from './contentWriterWorkspace.logic';

const DEFAULT_TIMEZONE = 'Asia/Riyadh';

type PanelState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'generating' }
  | {
      readonly kind: 'preview';
      readonly data: GenerateWriterAiDraftsData;
      // Codex round 2 (P2) — bind the preview to the client_id it was
      // generated for so a client swap after the fact cannot compose
      // client B's id with client A's ai_run_id. Save reads this
      // instead of the current picker value.
      readonly clientId: string;
      // Codex round 10 (P2) — a save failure PRESERVES the preview so
      // the writer can retry the same paid ai_run without burning
      // budget on a fresh generation. The save error rides alongside
      // the preview instead of replacing it.
      readonly saveError?: string;
    }
  | {
      readonly kind: 'saving';
      readonly data: GenerateWriterAiDraftsData;
      readonly clientId: string;
    }
  | { readonly kind: 'saved' }
  | { readonly kind: 'error'; readonly message: string };

const WRITER_AI_PLATFORMS: ReadonlyArray<ContentVariantPlatform> = [
  'instagram_feed',
  'instagram_reel',
  'instagram_story',
  'snapchat_story',
  'tiktok_video',
];

const MODES: ReadonlyArray<{
  readonly key: 'unified' | 'platform_adapted';
  readonly label: string;
}> = [
  { key: 'unified', label: 'موحّد (نصّ واحد لكل المنصّات)' },
  { key: 'platform_adapted', label: 'مخصّص لكل منصّة' },
];

const ENTRY_COUNT_DEFAULT = 4;

interface Props {
  readonly me: MeContext;
  /** Called after a successful save so the parent can refresh + navigate. */
  readonly onSaved: (firstTaskId: string) => void;
}

export function ContentWriterAiPanel({ me, onSaved }: Props): JSX.Element {
  const eligibleClients = activeAssignedClientsForWriter(me.client_assignments);
  const defaultClientId = eligibleClients[0]?.client_id ?? null;
  // Codex round 1 (P2) — default to the writer's OWN timezone, not the
  // page timezone or UTC. Around a month boundary (e.g. past midnight
  // in Damascus while UTC is still on the previous day) UTC picks the
  // wrong month and the generation fails validation against a stale
  // pattern check the user did not mean to set.
  const activeTimezone = me.timezone || DEFAULT_TIMEZONE;

  const [clientId, setClientId] = useState<string | null>(defaultClientId);
  const [month, setMonth] = useState<string>(currentMonthYYYYMM(activeTimezone));
  const [entryCount, setEntryCount] = useState<number>(ENTRY_COUNT_DEFAULT);
  const [platforms, setPlatforms] = useState<ReadonlyArray<ContentVariantPlatform>>([
    'instagram_feed',
  ]);
  const [mode, setMode] = useState<'unified' | 'platform_adapted'>('unified');
  const [instruction, setInstruction] = useState<string>('');
  const [expanded, setExpanded] = useState<boolean>(false);
  const [state, setState] = useState<PanelState>({ kind: 'idle' });
  // Codex round 3 (P2) — monotonic generation nonce. A completed
  // response only lands as a preview if the nonce it left with still
  // matches the current one; otherwise the writer changed clients (or
  // fired another generate) after issuing the request, and the stale
  // response must be dropped rather than installed against the wrong
  // client. A useRef is preferred over a useState because we read it
  // from the async completion closure without needing a re-render.
  const generationNonceRef = useRef(0);

  const canSubmit =
    clientId !== null &&
    platforms.length >= 1 &&
    platforms.length <= 4 &&
    entryCount >= 1 &&
    state.kind !== 'generating' &&
    state.kind !== 'saving';

  // Codex round 2 (P2) — switching the picker to a different client
  // must discard any preview generated for the previous client, or a
  // subsequent save would compose the NEW client_id with the OLD
  // ai_run_id and the RPC's provenance check would refuse it.
  //
  // Codex round 3 (P2) — the same applies to an IN-FLIGHT generation:
  // if the writer swaps clients while generate is running, its response
  // (issued against the previous client) must not install a preview.
  // Bumping the nonce here causes the pending completion to see a
  // stale nonce and drop.
  function selectClient(nextClientId: string): void {
    setClientId(nextClientId);
    generationNonceRef.current += 1;
    setState((prev) => {
      if (prev.kind === 'preview' && prev.clientId !== nextClientId) {
        return { kind: 'idle' };
      }
      if (prev.kind === 'generating') {
        // A pending generation was for a different client (or the same
        // one — either way the completion is now stale relative to the
        // bumped nonce). Return to idle so the user can retry.
        return { kind: 'idle' };
      }
      return prev;
    });
  }

  async function handleGenerate() {
    if (!clientId) return;
    const requestedClientId = clientId;
    const requestNonce = ++generationNonceRef.current;
    setState({ kind: 'generating' });
    try {
      const data = await generateWriterAiDrafts({
        input: {
          client_id: requestedClientId,
          mode,
          month,
          platforms,
          entry_count: entryCount,
          instruction: instruction.trim() === '' ? null : instruction.trim(),
          timezone: activeTimezone,
        },
      });
      if (generationNonceRef.current !== requestNonce) {
        // A newer generate started or the client changed while this
        // one was in flight — do not install the stale preview.
        return;
      }
      setState({ kind: 'preview', data, clientId: requestedClientId });
    } catch (err) {
      if (generationNonceRef.current !== requestNonce) return;
      setState({ kind: 'error', message: errorMessage(err) });
    }
  }

  async function handleSave(data: GenerateWriterAiDraftsData, previewClientId: string) {
    // Save uses the client_id the preview was BOUND to, not the current
    // picker value — the two can differ if the writer switched clients
    // between generate and save. selectClient() already clears the
    // preview on such a switch; this is defence-in-depth.
    setState({ kind: 'saving', data, clientId: previewClientId });
    try {
      const payload = buildWriterAiSaveItems(data);
      const result = await saveWriterAiCalendarDraft({
        input: {
          client_id: previewClientId,
          ai_run_id: data.ai_run_id,
          mode: data.mode,
          items: payload.items,
        },
      });
      const firstTaskId = result.items[0]?.writing_task_id ?? null;
      setState({ kind: 'saved' });
      setExpanded(false);
      if (firstTaskId) onSaved(firstTaskId);
    } catch (err) {
      // Codex round 10 (P2) — DO NOT destroy the preview. A transient
      // network / server error would otherwise force the writer to
      // regenerate (paid AI). Keep the preview and its ai_run_id so
      // "Save" can be retried against the same run.
      setState({
        kind: 'preview',
        data,
        clientId: previewClientId,
        saveError: errorMessage(err),
      });
    }
  }

  if (eligibleClients.length === 0) {
    return (
      <section className="cww-ai-panel" aria-labelledby="cww-ai-h">
        <div className="cww-ai-head">
          <span id="cww-ai-h" className="cww-ai-t">
            {WRITER_AI_TITLE_AR}
          </span>
        </div>
        <div className="occ-empty sm">{WRITER_AI_NO_CLIENTS_AR}</div>
      </section>
    );
  }

  return (
    <section className="cww-ai-panel" aria-labelledby="cww-ai-h">
      <div className="cww-ai-head">
        <span id="cww-ai-h" className="cww-ai-t">
          {WRITER_AI_TITLE_AR}
        </span>
        <button
          type="button"
          className="dwk-act"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'إغلاق' : 'فتح النموذج'}
        </button>
      </div>
      {expanded && (
        <div className="cww-ai-body">
          {eligibleClients.length > 1 ? (
            <fieldset className="cww-ai-fieldset">
              <legend>{WRITER_AI_PICK_CLIENT_AR}</legend>
              {eligibleClients.map((c) => (
                <ClientRadio
                  key={c.client_id}
                  client={c}
                  selected={c.client_id === clientId}
                  onSelect={() => selectClient(c.client_id)}
                />
              ))}
            </fieldset>
          ) : (
            <div className="cww-ai-single-client">
              العميل: <strong>{eligibleClients[0]!.client_name}</strong>
            </div>
          )}

          <label className="cww-ai-field">
            <span>الشهر (YYYY-MM)</span>
            <input
              type="text"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              pattern="^\d{4}-(0[1-9]|1[0-2])$"
            />
          </label>

          <label className="cww-ai-field">
            <span>عدد الأفكار (1..30)</span>
            <input
              type="number"
              min={1}
              max={30}
              value={entryCount}
              onChange={(e) => setEntryCount(clampInt(e.target.value, 1, 30, ENTRY_COUNT_DEFAULT))}
            />
          </label>

          <fieldset className="cww-ai-fieldset">
            <legend>المنصّات (1..4)</legend>
            {WRITER_AI_PLATFORMS.map((p) => (
              <label key={p} className="cww-ai-checkbox">
                <input
                  type="checkbox"
                  checked={platforms.includes(p)}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setPlatforms((prev) => {
                      const set = new Set(prev);
                      if (checked) set.add(p);
                      else set.delete(p);
                      return Array.from(set).slice(0, 4);
                    });
                  }}
                />
                {platformLabelAr(p)}
              </label>
            ))}
          </fieldset>

          <fieldset className="cww-ai-fieldset">
            <legend>الوضع</legend>
            {MODES.map((m) => (
              <label key={m.key} className="cww-ai-radio">
                <input
                  type="radio"
                  name="cww-ai-mode"
                  value={m.key}
                  checked={mode === m.key}
                  onChange={() => setMode(m.key)}
                />
                {m.label}
              </label>
            ))}
          </fieldset>

          <label className="cww-ai-field">
            <span>توجيه إضافي (اختياري)</span>
            <textarea
              rows={3}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              maxLength={2000}
            />
          </label>

          <div className="dwk-actions">
            <button
              type="button"
              className="dwk-act primary"
              disabled={!canSubmit}
              onClick={handleGenerate}
            >
              {state.kind === 'generating' ? WRITER_AI_GENERATING_AR : 'ابدأ التوليد'}
            </button>
          </div>

          {state.kind === 'preview' && (
            <>
              <PreviewBlock
                data={state.data}
                onSave={() => handleSave(state.data, state.clientId)}
                onDiscard={() => setState({ kind: 'idle' })}
              />
              {state.saveError !== undefined && (
                <div className="dwk-act-msg err" role="alert">
                  {state.saveError}
                </div>
              )}
            </>
          )}
          {state.kind === 'saving' && <div className="dwk-act-msg">{WRITER_AI_SAVING_AR}</div>}
          {state.kind === 'saved' && <div className="dwk-act-msg ok">{WRITER_AI_SUCCESS_AR}</div>}
          {state.kind === 'error' && <div className="dwk-act-msg err">{state.message}</div>}
        </div>
      )}
    </section>
  );
}

function ClientRadio({
  client,
  selected,
  onSelect,
}: {
  readonly client: ClientAssignmentSummary;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  return (
    <label className="cww-ai-radio">
      <input
        type="radio"
        name="cww-ai-client"
        value={client.client_id}
        checked={selected}
        onChange={onSelect}
      />
      {client.client_name}
    </label>
  );
}

function PreviewBlock({
  data,
  onSave,
  onDiscard,
}: {
  readonly data: GenerateWriterAiDraftsData;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
}): JSX.Element {
  const items =
    data.mode === 'unified'
      ? data.draft.items.map((it) => ({
          title: it.title,
          date: it.scheduled_date,
          caption: it.caption,
        }))
      : data.draft.items.map((it) => ({
          title: it.title,
          date: it.scheduled_date,
          caption: it.variants[0]?.caption ?? '',
        }));
  return (
    <div className="cww-ai-preview">
      <div className="cww-ai-preview-h">
        عرض المسودّة ({data.mode === 'unified' ? 'موحّد' : 'مخصّص'})
      </div>
      <ul className="cww-ai-preview-list">
        {items.map((it, idx) => (
          <li key={idx}>
            <strong>{it.title}</strong>
            <span className="cww-ai-preview-date"> · {it.date}</span>
            <div className="cww-ai-preview-caption">{truncate(it.caption, 200)}</div>
          </li>
        ))}
      </ul>
      <div className="dwk-actions">
        <button type="button" className="dwk-act dark" onClick={onDiscard}>
          إلغاء
        </button>
        <button type="button" className="dwk-act primary" onClick={onSave}>
          حفظ وإرسال إلى مساحة الكتابة
        </button>
      </div>
    </div>
  );
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim().length > 0) return err.message;
  return WRITER_AI_GENERIC_ERROR_AR;
}
